import React, { useState, useEffect } from 'react';
import { Form, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import { VehicleFilters as VehicleFiltersType } from '../../../types/Vehicle';
import FiltersCard from '../../../components/FiltersCard';

interface VehicleFiltersProps {
  filters: VehicleFiltersType;
  filterOptions: {
    currentStatuses: string[];
    serviceAreas: { id: number; name: string; code?: string }[];
    vehicleClassifications: string[];
    currentYards: { id: number; name: string; code?: string }[];
  };
  onFilterChange: (filters: VehicleFiltersType) => void;
  /** The page's search, in the panel's header bar (V2-077); Enter runs it within the filters. */
  search: SearchFieldProps;
  /** True when a search is applied: Clear All then has something to clear. */
  searchApplied?: boolean;
  /** Clear All: filters and search back to the defaults. */
  onClear: () => void;
}

/** Defaults: no filter. In service / Out of service is temporary, so it is not "inactive" (INACT-05). */
export const EMPTY_VEHICLE_FILTERS: VehicleFiltersType = {
  currentStatus: '',
  serviceArea: '',
  vehicleClassification: '',
  currentYard: '',
};

const VehicleFilters: React.FC<VehicleFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  search,
  searchApplied = false,
  onClear,
}) => {
  const [localFilters, setLocalFilters] = useState<VehicleFiltersType>(filters);

  // Update local filters when props change (e.g., when cleared externally)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (name: keyof VehicleFiltersType, value: string) => {
    setLocalFilters({
      ...localFilters,
      [name]: value
    });
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  // Status and Service Area stay single-select: GET /api/vehicles compares `currentStatus` with
  // equality and parseInt()s `serviceArea` (server/src/modules/vehicle/vehicleListWhere.utils.ts:14-15,22-23).
  // No default hides vehicles, so "n filters applied" is the filters that are set.
  const activeFilterCount = [filters.currentStatus, filters.serviceArea, filters.vehicleClassification, filters.currentYard].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0 || searchApplied;
  const hasPendingChanges =
    (localFilters.currentStatus || '') !== (filters.currentStatus || '') ||
    (localFilters.serviceArea || '') !== (filters.serviceArea || '') ||
    (localFilters.vehicleClassification || '') !== (filters.vehicleClassification || '') ||
    (localFilters.currentYard || '') !== (filters.currentYard || '');

  const handleClearFilters = () => {
    setLocalFilters(EMPTY_VEHICLE_FILTERS);
    onClear();
  };

  return (
    <FiltersCard
      title="Vehicle Filters"
      subtitle="Filter vehicles by status, service area, and classification"
      collapsible
      defaultCollapsed={false}
      appliedFilterCount={activeFilterCount}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters}
      search={search}
      onApply={handleApplyFilters}
      applyDisabled={!hasPendingChanges}
    >
      <FilterGrid>
        <FilterField>
          <Form.Label htmlFor="vehicle-filter-status">Current Status</Form.Label>
          <Form.Select
            id="vehicle-filter-status"
            value={localFilters.currentStatus || ''}
            onChange={(e) => handleFilterChange('currentStatus', e.target.value)}
          >
            <option value="">All Statuses</option>
            {filterOptions.currentStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="vehicle-filter-service-area">Service Area</Form.Label>
          <Form.Select
            id="vehicle-filter-service-area"
            value={localFilters.serviceArea || ''}
            onChange={(e) => handleFilterChange('serviceArea', e.target.value)}
          >
            <option value="">All Service Areas</option>
            {filterOptions.serviceAreas.map(area => (
              <option key={area.id} value={area.id.toString()}>{area.name}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="vehicle-filter-current-yard">Current Yard</Form.Label>
          <Form.Select
            id="vehicle-filter-current-yard"
            value={localFilters.currentYard || ''}
            onChange={(e) => handleFilterChange('currentYard', e.target.value)}
          >
            <option value="">All Yards</option>
            {filterOptions.currentYards.map(yard => (
              <option key={yard.id} value={yard.id.toString()}>{yard.name}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="vehicle-filter-classification">Vehicle Classification</Form.Label>
          <Form.Select
            id="vehicle-filter-classification"
            value={localFilters.vehicleClassification || ''}
            onChange={(e) => handleFilterChange('vehicleClassification', e.target.value)}
          >
            <option value="">All Classifications</option>
            {filterOptions.vehicleClassifications.map(classification => (
              <option key={classification} value={classification}>{classification}</option>
            ))}
          </Form.Select>
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default VehicleFilters;

